import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Field } from '../ui/Field.tsx';
import { describeError } from '../ui/problemDetails.ts';
import { useAuth } from './useAuth.ts';

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Send people back where they were headed before they were bounced to sign in.
  const returnTo = (location.state as { from?: string } | null)?.from ?? '/';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await signIn(email.trim(), password);
      await navigate(returnTo, { replace: true });
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="app-shell app-shell--centred">
      <header className="auth-header">
        <h1>Listocial</h1>
        <p className="muted">Shared shopping lists that update live.</p>
      </header>

      <form className="card stack" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <h2>Sign in</h2>

        <Field
          label="Email"
          type="email"
          name="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          spellCheck={false}
          required
        />

        <Field
          label="Password"
          type="password"
          name="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        <button className="button button--primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="muted centred">
          No account yet? <Link to="/register">Create one</Link>
        </p>
      </form>
    </main>
  );
}
